/**
 * Starting a second match with a team you have already entered.
 *
 * PRD MS-2 budgets **twenty seconds** for this against sixty for the first match, and the
 * whole difference is not re-typing eleven names. The saved squad comes from the server
 * (`GET /api/teams`); what does *not* come from the server is which of those players were
 * in the XI last time and in what order — nothing stores that, and it is the remaining
 * work between "the names are here" and "tap start".
 *
 * So it is remembered locally, per team. `localStorage` is the honest home for it: it is a
 * convenience for this device, not a fact about the match, and a wrong guess costs one tap
 * to fix. Storing it server-side would make it look authoritative and would need a schema
 * change for a preference — and if the scorer changes device, he re-picks once, which is
 * exactly the cost of the thing being local.
 */
import type { PlayerId, Team, TeamId } from "@sportsos/engine";
import type { Store } from "../localStore.ts";
import { savedDefaults } from "./setup.ts";
import type { SquadPlayer, TeamSetup } from "./setup.ts";
import { xi } from "./setup.ts";

export interface RememberedXi {
  /** Player ids in the batting order last used, XI only. */
  readonly order: readonly PlayerId[];
  readonly keeperId: PlayerId | null;
}

const key = (teamId: TeamId): string => `sportsos.xi.${teamId}`;

export function readRememberedXi(store: Store, teamId: TeamId): RememberedXi | null {
  const raw = store.getItem(key(teamId));
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<RememberedXi>;
    if (!Array.isArray(parsed.order)) return null;
    return { order: parsed.order, keeperId: parsed.keeperId ?? null };
  } catch {
    return null;
  }
}

export function rememberXi(store: Store, team: TeamSetup): void {
  const remembered: RememberedXi = {
    // `xi()` is the one definition of "the XI, in batting order" (setup.ts). Re-filtering
    // and re-sorting here would be a second copy of it, free to drift.
    order: xi(team).map((player) => player.id),
    keeperId: team.keeperId,
  };
  try {
    store.setItem(key(team.id), JSON.stringify(remembered));
  } catch {
    // A full quota costs the scorer one re-pick next time. Not worth failing a match over.
  }
}

/**
 * Build a `TeamSetup` from a saved team, with last time's XI preselected.
 *
 * The squad is *whoever is in the team now*, and the remembered order is applied on top —
 * never the other way round. That ordering matters in both directions:
 *
 * - A player added to the team since the last match is not in the remembered list. He
 *   still appears, at the bottom, out of the XI — silently dropping him would hide a
 *   player the scorer deliberately added.
 * - A player removed from the team is in the remembered list and no longer in the squad.
 *   He is skipped, not resurrected, or the XI would name someone the server will reject.
 *
 * With nothing remembered, everyone is in the XI in squad order — the same default the
 * first-match path uses, because a scorer unticking two names is faster than ticking nine.
 */
export function teamSetupFromSaved(team: Team, remembered: RememberedXi | null, maxXi?: number): TeamSetup {
  const squad = new Map(team.squad.map((player) => [String(player.id), player]));
  const ordered: SquadPlayer[] = [];
  const cap = maxXi ?? Infinity;
  let inXiCount = 0;

  for (const playerId of remembered?.order ?? []) {
    const player = squad.get(String(playerId));
    if (player === undefined) continue;
    squad.delete(String(playerId));
    const inXi = inXiCount < cap;
    if (inXi) inXiCount++;
    ordered.push({
      id: player.id,
      name: player.displayName,
      battingOrder: ordered.length + 1,
      inXi,
      existing: true,
      ...savedDefaults(player),
    });
  }

  for (const player of squad.values()) {
    const inXi = remembered === null && inXiCount < cap;
    if (inXi) inXiCount++;
    ordered.push({
      id: player.id,
      name: player.displayName,
      battingOrder: ordered.length + 1,
      inXi,
      existing: true,
      ...savedDefaults(player),
    });
  }

  // A remembered keeper who has left the squad is dropped rather than kept as a dangling
  // id — `Delivery.keeperId` is what puts the † in `st †Khan b Sharma`, and pointing it at
  // someone not playing would render a dagger against a name nobody can find.
  const keeperId =
    remembered?.keeperId !== null && remembered?.keeperId !== undefined
      ? (ordered.find((player) => player.id === remembered.keeperId)?.id ?? null)
      : null;

  return { id: team.id, name: team.name, players: ordered, keeperId, captainId: null, existing: true };
}
